import React from 'react';
import range from 'lodash/range';
import { Flex, Div } from '../box';
import Icon from '../icon';
import Text from '../text';
import PaginationButton from './pagination-button';

export interface pagerData {
  page: number;
}

export interface PagerProps {
  amountOfPages?: number;
  pagesPadding?: number;
  currentPage?: number;
  onChange: ({ page }: pagerData) => void;
  disabled?: boolean;
  showEdgeButtons?: boolean;
}

const Pager = ({
  pagesPadding = 2,
  amountOfPages = 1,
  onChange,
  disabled,
  currentPage = 1,
  showEdgeButtons = false,
}: PagerProps): React.JSX.Element => {
  const getNumbers = (): React.JSX.Element[] => {
    let rangeStart = currentPage - pagesPadding;
    let rangeEnd = currentPage + pagesPadding;

    if (rangeEnd > amountOfPages) {
      rangeEnd = amountOfPages;
      rangeStart = amountOfPages - pagesPadding * 2;
      rangeStart = rangeStart < 1 ? 1 : rangeStart;
    }

    if (rangeStart <= 1) {
      rangeStart = 1;
      rangeEnd = Math.min(pagesPadding * 2 + 1, amountOfPages);
    }
    const buttons = range(rangeStart - 1, rangeEnd).map(item => {
      const buttonData = { display: item + 1 };
      const isSelected = currentPage === buttonData.display;

      return (
        <PaginationButton
          data-testid={'numeric-button'}
          disabled={disabled}
          aria-label={`load page ${buttonData.display}`}
          active={isSelected}
          key={buttonData.display}
          aria-selected={isSelected}
          variant="secondary"
          size={'sm'}
          onClick={() => {
            onChange({ page: buttonData.display });
          }}
        >
          {buttonData.display}
        </PaginationButton>
      );
    });
    return buttons;
  };

  const disableLeftArrow = currentPage === 1 || disabled;
  const disableRightArrow = currentPage === amountOfPages || disabled;
  const showEllipses = amountOfPages > 2 * pagesPadding + 1;

  return (
    <Flex role={'navigation'} alignItems="center" justifyContent="center" gap="xs">
      {showEdgeButtons && (
        <PaginationButton
          size={'xs'}
          disabled={disableLeftArrow}
          onClick={() => {
            onChange({ page: 1 });
          }}
          variant="secondary-clean"
          aria-label="first page"
        >
          <Icon name={'DOUBLE_ANGLE_LEFT'} size={'xl'} />
        </PaginationButton>
      )}
      <PaginationButton
        disabled={disableLeftArrow}
        onClick={() => {
          onChange({ page: currentPage - 1 });
        }}
        size={'xs'}
        variant="secondary"
        aria-label="previous page"
      >
        <Icon name={'ANGLE_LEFT'} size={'xl'} />
      </PaginationButton>

      {showEllipses && currentPage > pagesPadding + 1 ? (
        <Div paddingBottom={'xs'} width={6} aria-hidden={true}>
          <Text size={'xs'} data-testid={'filler-dots'}>
            ...
          </Text>
        </Div>
      ) : null}

      {pagesPadding !== 0 && getNumbers()}

      {showEllipses && currentPage < amountOfPages - pagesPadding ? (
        <Div paddingBottom={'xs'} width={6} aria-hidden={true}>
          <Text size={'xs'} data-testid={'filler-dots'}>
            ...
          </Text>
        </Div>
      ) : null}

      <PaginationButton
        size={'xs'}
        disabled={disableRightArrow}
        onClick={() => {
          onChange({ page: currentPage + 1 });
        }}
        variant="secondary"
        aria-label="next page"
      >
        <Icon name={'ANGLE_RIGHT'} size={'xl'} />
      </PaginationButton>
      {showEdgeButtons && (
        <PaginationButton
          size={'xs'}
          disabled={disableRightArrow}
          onClick={() => {
            onChange({ page: amountOfPages });
          }}
          variant="secondary-clean"
          aria-label="last page"
        >
          <Icon name={'DOUBLE_ANGLE_RIGHT'} size={'xl'} />
        </PaginationButton>
      )}
    </Flex>
  );
};

export default Pager;
